<section>
  <h1 class="mb-3">Locations</h1>
  <a *removeIfUnauthorized="canCreate" (click)="newLocation()" class="btn btn-active mb-3">Add location</a>
  <div class="card-group mb-4">
    <div class="card">
      <div #tgt id="filterBody" class="card-body">
        <h2 class="card-title h5">RMS Locations List</h2>
        <p class="mb-2 card-text font-size-sm">View a list of locations filtered by event date range<ng-container *ngIf="!isStateLocalLE"> and geographic region of the agency investigating the associated RMS event(s)</ng-container>.</p>
        <p *ngIf="!isStateLocalLE" class="mb-2 card-text font-size-sm">Default geographic filter preferences can be set in your <a [routerLink]="['/account/userprofile']">user profile</a>.</p>
        <ness-date-range-filter #df [suppressUI]="true" [dateFilterStorageKey]="locationsStorageKey"></ness-date-range-filter>
        <ng-container *removeIfNoPermission="canView">
          <ness-geographic-filter #gf [suppressUI]="true" [geoFilterStorageKey]="geoFilterStorageKey"  (isLoading)="loading = $event"></ness-geographic-filter>
        </ng-container>
        <div class="mt-3 d-flex align-items-start flex-wrap flex-md-nowrap">
          <div class="font-size-sm flex-grow-1 me-3 mb-2"><i class="fas fa-info-circle text-active" aria-hidden="true"></i> Locations not associated with an RMS event in NESS will not be included in the filter results.</div>
          <button type="button" class="btn btn-active btn-sm text-nowrap mb-2" [disabled]="loading" [attr.aria-disabled]="loading" (click)="applyFilters()" title="Apply filters">View List<span class="visually-hidden"> filters</span></button>
          <button type="button" class="btn btn-secondary btn-sm text-nowrap mb-2" [disabled]="loading" [attr.aria-disabled]="loading" (click)="resetDefaultFilters()" title="Reset filters to defaults">Reset<span class="visually-hidden"> filters to defaults</span></button>
        </div>
      </div>
    </div>
    <div class="align-self-center text-center my-3 mx-4" style="width: 2rem;"><span class="visually-hidden">Filter locations by event date range and investigating agency region, </span><span class="fw-bold">OR</span> <span class="visually-hidden">search for locations using selected criteria.</span></div>
    <div class="card">
      <div class="card-body">
        <h2 class="card-title h5">RMS Locations Search</h2>
        <p class="mb-2 card-text font-size-sm">Search for any location in NESS based on the criteria. This will return all matching location records in NESS.</p>
        <form [formGroup]="findLocationFormGroup">
          <div class="row g-2 flex-row">
            <div class="col-lg-8 col-12">
              <label for="FindLocationStreetAddressID" class="form-label required-field">Street address:</label>
              <input id="FindLocationStreetAddressID" type="text" formControlName="FindLocationStreetAddressID" class="form-control form-control-sm" required aria-required="true" />
              <div class="invalid-feedback">
                Please enter a street address. <ng-container *ngIf="findLocationFormGroup.get('FindLocationStreetAddressID').hasError('maxlength')">The street address cannot exceed 80 characters.</ng-container>
              </div>
            </div>
            <div class="col-lg-4 col-12">
              <label class="form-label" for="FindLocationUnitID">Unit:</label>
              <input id="FindLocationUnitID" type="text" formControlName="FindLocationUnitID" class="form-control form-control-sm" />
              <div class="invalid-feedback" *ngIf="findLocationFormGroup.get('FindLocationUnitID').hasError('maxlength')">
                The unit cannot exceed 80 characters.
              </div>
            </div>
            <div class="col-lg-6 col-12">
              <label class="form-label" for="FindLocationCityNameID">City:</label>
              <input id="FindLocationCityNameID" type="text" formControlName="FindLocationCityNameID" class="form-control form-control-sm" />
            </div>
            <div class="col-lg-6 col-12">
              <label class="form-label" for="FindLocationStateID">State:</label>
              <select class="form-select form-select-sm" id="FindLocationStateID" formControlName="FindLocationStateID">
                <option value="">Select state</option>
                <ng-container *ngIf="lookupService.usStates$ | async as usStates">
                  <option *ngFor="let state of usStates" [value]="state.StateAbbreviation">{{state.StateName}}</option>
                </ng-container>
              </select>
            </div>
            <div class="col-lg-5 col-12">
              <label class="form-label" for="FindLocationPostalCodeID">Postal code:</label>
              <p-inputMask inputId="FindLocationPostalCodeID" mask="99999" formControlName="FindLocationPostalCodeID" styleClass="form-control form-control-sm" [autoClear]="true" (onBlur)="onMaskBlur('FindLocationPostalCodeID')"></p-inputMask>
              <div class="invalid-feedback" *ngIf="findLocationFormGroup.get('FindLocationPostalCodeID').hasError('pattern')">
                The postal code at least contain 5 digits.
              </div>
            </div>
            <div class="col-lg-5 col-12">
              <label class="form-label" for="FindLocationPostalCodeExtendedID">Postal code extended:</label>
              <p-inputMask inputId="FindLocationPostalCodeExtendedID" mask="9999" formControlName="FindLocationPostalCodeExtendedID" styleClass="form-control form-control-sm" [autoClear]="true" (onBlur)="onMaskBlur('FindLocationPostalCodeExtendedID')"></p-inputMask>
              <div class="invalid-feedback" *ngIf="findLocationFormGroup.get('FindLocationPostalCodeExtendedID').hasError('pattern')">
                The postal code extended must contain 4 digits.
              </div>
            </div>
            <div class="col-lg-2 col-12 align-self-end"><button [disabled]="findLocationFormGroup.invalid" type="button" (click)="onSearchLocation()" class="btn btn-sm btn-active mt-3">Search</button></div>
          </div>
        </form>
      </div>
    </div>
  </div>
  <p-messages key="locationsMsgs" [closable]="true"></p-messages>
  <ng-container *ngIf="showSearchResults">
    <p-table *ngIf="addressResults$ | async as addressResults; else loadingSearch" #dt2 [columns]="searchCols" [value]="addressResults" selectionMode="single" [(selection)]="selectedAddress" [paginator]="true" [alwaysShowPaginator]="false" [rows]="rows" [autoLayout]="true" responsiveLayout="scroll">
      <ng-template pTemplate="caption">
        <div class="p-2 fw-medium text-center">Find existing location result</div>
      </ng-template>
      <ng-template pTemplate="header" let-columns>
        <tr>
          <th scope="col" *ngFor="let col of columns" [pSortableColumn]="col.field">
            {{col.header}}
            <p-sortIcon [field]="col.field" ariaLabel="Activate to sort" ariaLabelDesc="Activate to sort in descending order" ariaLabelAsc="Activate to sort in ascending order"></p-sortIcon>
          </th>
        </tr>
      </ng-template>
      <ng-template pTemplate="body" let-rowData let-columns="columns">
        <tr [pSelectableRow]="rowData">
          <ng-container *ngFor="let col of columns">
            <th scope="row" *ngIf="col.header==='Address';else sddefault"><button class="btn btn-link px-0" (click)="viewDetails(rowData.AddressID)" aria-label="{{'View address '+rowData.StreetAddressWithUnit+' details'}}" tabindex="0">{{rowData['StreetAddressWithUnit'] }}</button></th>
            <ng-template #sddefault>
              <td>{{rowData[col.field]}}</td>
            </ng-template>
          </ng-container>
        </tr>
      </ng-template>
      <ng-template pTemplate="emptymessage" let-columns>
        <tr>
          <td [attr.colspan]="columns.length">
            No records found
          </td>
        </tr>
      </ng-template>
    </p-table>
    <ng-template #loadingSearch><div class="m-4">Loading search results...</div></ng-template>
  </ng-container>
  <ng-container *ngIf="showFilterResults">
    <p-table #dt [columns]="cols" [value]="addresses" selectionMode="single" [(selection)]="selectedAddress" [paginator]="true" [alwaysShowPaginator]="false" [rows]="rows" [autoLayout]="true" responsiveLayout="scroll">
      <ng-template pTemplate="caption">
        <ness-paged-loading-status *ngIf="loading" [startRow]="loadingStartRow$ | async" [endRow]="loadingEndRow$ | async" [messageTemplate]="messageTemplate"></ness-paged-loading-status>
        <div class="d-flex flex-wrap p-2 mx-2 filter-status font-size-sm">
          <div class="p-2 me-2">Showing:</div>
          <ness-date-range-filter-status *ngIf="!showAllLocations"></ness-date-range-filter-status>
          <ng-container *removeIfNoPermission="canView">
            <ness-geographic-filter-status *ngIf="!showAllLocations"></ness-geographic-filter-status>
          </ng-container>
          <div *ngIf="showAllLocations" class="ness-filter-tag p-2 me-2 mb-2">All locations</div>
          <button type="button" class="btn btn-success mb-2 me-2" [disabled]="loading || showAllLocations" [attr.aria-disabled]="loading || showAllLocations" title="Clear applied filters" (click)="clearAppliedFilters()">Clear <span class="visually-hidden">applied filters</span></button>
          <div class="ms-md-2 ms-lg-auto me-2">
            <div class="input-group">
              <span class="input-group-text"><i class="fa fa-search" aria-hidden="true"></i></span>
              <input type="text" class="form-control" name="locationsGridGlobalFilter" placeholder="Locations grid filter" (input)="dt.filterGlobal($event['target']['value'], 'contains')" aria-label="Globally filter the locations grid">
            </div>
          </div>
        </div>
      </ng-template>
      <ng-template pTemplate="header" let-columns>
        <tr>
          <th scope="col" *ngFor="let col of columns" [pSortableColumn]="col.field">
            {{col.header}}
            <p-sortIcon [field]="col.field" ariaLabel="Activate to sort" ariaLabelDesc="Activate to sort in descending order" ariaLabelAsc="Activate to sort in ascending order"></p-sortIcon>
          </th>
        </tr>
        <tr>
          <th scope="col" *ngFor="let col of columns" [ngSwitch]="col.field">
            <label class="visually-hidden" for="filter{{col.field}}">Filter by {{col.header}} column</label>
            <input *ngSwitchCase="'StreetAddress'" id="filter{{col.field}}" type="text" class="form-control form-control-sm" tabindex="0" (focus)="fixFilterTable()" (input)="dt.filter($event['target']['value'], col.field, 'streetAddress')" />
            <input *ngSwitchDefault id="filter{{col.field}}" class="form-control form-control-sm" type="text" tabindex="0" (input)="dt.filter($event['target']['value'], col.field, col.filterMatchMode)">
          </th>
        </tr>
      </ng-template>
      <ng-template pTemplate="body" let-rowData let-columns="columns">
        <tr [pSelectableRow]="rowData">
          <ng-container *ngFor="let col of columns">
            <th scope="row" *ngIf="col.header==='Address';else tddefault">
              <button class="btn btn-link px-0" (click)="viewDetails(rowData.AddressID)" aria-label="View location details" tabindex="0">{{rowData['StreetAddress'] }}</button>
            </th>
            <ng-template #tddefault>
              <td>{{rowData[col.field]}}</td>
            </ng-template>
          </ng-container>
        </tr>
      </ng-template>
      <ng-template pTemplate="emptymessage" let-columns>
        <tr>
          <td [attr.colspan]="columns.length">
            No location found.
          </td>
        </tr>
      </ng-template>
    </p-table>
  </ng-container>
</section>
<p-sidebar (onShow)="onShowSidebar()" (onHide)="onCloseSidebar()" [(visible)]="openLocationViewDetailForm" position="right" [blockScroll]="true" styleClass="p-sidebar-lg" [showCloseIcon]="false">
  <ng-container *ngIf="address$ | async as address;else loadingDetails">
    <ness-location-detail *ngIf="showLocationDetailsView" [addressDTO]="address" [hasDeletePermission]="hasDeletePermission" [hasEditPermission]="hasEditPermission" (deleteLocationCompleted)="deleteLocationCompleted($event)" (editLocationLocationClicked)="showEditLocation($event)"></ness-location-detail>
    <ness-location-form *ngIf="showEditRMSLocationView" [titleText]="'Edit location'" [isEventLocationForm]="false" [isEditForm]="true" [locationFormData]="address" (closeForm)="onCloseLocation()" (locationSaved)="onLocationFormSaved($event)"></ness-location-form>
    <button type="button" *ngIf="showLocationDetailsView" (click)="onCloseLocation()" class="btn btn-active my-3">Close</button>
  </ng-container>
  <ng-template #loadingDetails>
    <div class="m-3">Loading location details...</div>
  </ng-template>
</p-sidebar>

